// GENERATED by scripts/room-layout/sync-geometry.mjs. Edit canonical shared-ux geometry only.
/** Canonical room geometry. DOM-free; mirrored into the API and embeds by sync-geometry.mjs. */
export type RoomShape = 'square' | 'rectangle' | 'circle';
export type SpotSizePreset = 's' | 'm' | 'l';
export const SPOT_SIZE_PRESETS = { s: 's-v2', m: 'm-v2', l: 'l-v2' } as const;
export const SPOT_SIZES = ['s', 'm', 'l', 's-v2', 'm-v2', 'l-v2'] as const;
export type SpotSize = (typeof SPOT_SIZES)[number];
export const isSpotSize = (value: unknown): value is SpotSize =>
  SPOT_SIZES.some((size) => size === value);
export type Point = { x: number; y: number };
export type Bounds = { x: number; y: number; width: number; height: number };
export type Footprint = {
  shape: RoomShape;
  center: Point | null;
  angle: number;
  width: number;
  height: number;
};
export type SpotGeometry = Omit<Footprint, 'width' | 'height'> & {
  baseWidth: number;
  baseHeight: number;
};
export type LayoutObstruction = Footprint & { id: string; label: string };
export type OverlapAllowance = {
  ids: [string, string];
  footprints: [string, string];
};
export type RoomLayout = {
  version: 1;
  spotSize: SpotSize | null;
  spots: Record<string, SpotGeometry>;
  obstructions: LayoutObstruction[];
  bounds: Bounds;
  overlapAllowances: OverlapAllowance[];
};
export type LegacySpot = { id: string; x: number | null; y: number | null };
export type Collision = { ids: [string, string]; grandfathered: boolean };
export const GEOMETRY_EPSILON = 1e-6;
// Stored original presets retain their footprint; larger presets are opt-in.
export const SIZE_FACTORS: Record<SpotSize, number> = {
  s: 0.75,
  m: 1,
  l: 1.25,
  's-v2': 1.125,
  'm-v2': 1.5,
  'l-v2': 1.875,
};
const PADDING = 24;

export function canonicalDimensions(shape: RoomShape): {
  width: number;
  height: number;
} {
  return { width: shape === 'rectangle' ? 104 : 76, height: 76 };
}

export function normalizeNumber(value: number): number {
  if (!Number.isFinite(value)) throw new Error('Geometry must be finite');
  return Math.round(value * 1e6) / 1e6;
}

export function normalizeAngle(value: number): number {
  const angle = normalizeNumber(((value % 360) + 360) % 360);
  return angle === 360 ? 0 : angle;
}

export function spotFootprint(
  spot: SpotGeometry,
  size: SpotSize | null,
): Footprint {
  const scale = SIZE_FACTORS[size ?? 'm'];
  return {
    shape: spot.shape,
    center: spot.center,
    angle: spot.angle,
    width: spot.baseWidth * scale,
    height: spot.baseHeight * scale,
  };
}

export function newSpotGeometry(
  shape: RoomShape,
  center: Point | null = null,
): SpotGeometry {
  const { width, height } = canonicalDimensions(shape);
  return { shape, center, angle: 0, baseWidth: width, baseHeight: height };
}

export function changeSpotShape(
  spot: SpotGeometry,
  shape: RoomShape,
): SpotGeometry {
  return { ...newSpotGeometry(shape, spot.center), angle: spot.angle };
}

function axes(angle: number): [Point, Point] {
  const radians = (angle * Math.PI) / 180;
  const cos = Math.cos(radians),
    sin = Math.sin(radians);
  return [
    { x: cos, y: sin },
    { x: -sin, y: cos },
  ];
}

function dot(a: Point, b: Point): number {
  return a.x * b.x + a.y * b.y;
}

export function footprintBounds(footprint: Footprint): Bounds | null {
  if (!footprint.center) return null;
  const [a, b] = axes(footprint.angle);
  const halfWidth =
    footprint.shape === 'circle'
      ? footprint.width / 2
      : (Math.abs(a.x) * footprint.width + Math.abs(b.x) * footprint.height) /
        2;
  const halfHeight =
    footprint.shape === 'circle'
      ? footprint.height / 2
      : (Math.abs(a.y) * footprint.width + Math.abs(b.y) * footprint.height) /
        2;
  return {
    x: footprint.center.x - halfWidth,
    y: footprint.center.y - halfHeight,
    width: halfWidth * 2,
    height: halfHeight * 2,
  };
}

export function footprintSignature(footprint: Footprint): string {
  // Circles' rotation has no effect on their physical footprint.
  return JSON.stringify([
    footprint.shape,
    footprint.center?.x ?? null,
    footprint.center?.y ?? null,
    footprint.shape === 'circle' ? 0 : normalizeAngle(footprint.angle),
    footprint.width,
    footprint.height,
  ]);
}

/** True only when interiors penetrate by more than epsilon; touching edges are valid. */
export function footprintsOverlap(a: Footprint, b: Footprint): boolean {
  if (!a.center || !b.center) return false;
  const ab = footprintBounds(a)!,
    bb = footprintBounds(b)!;
  if (
    Math.min(ab.x + ab.width, bb.x + bb.width) - Math.max(ab.x, bb.x) <=
      GEOMETRY_EPSILON ||
    Math.min(ab.y + ab.height, bb.y + bb.height) - Math.max(ab.y, bb.y) <=
      GEOMETRY_EPSILON
  )
    return false;
  const delta = { x: b.center.x - a.center.x, y: b.center.y - a.center.y };
  if (a.shape === 'circle' && b.shape === 'circle') {
    return (
      Math.hypot(delta.x, delta.y) < (a.width + b.width) / 2 - GEOMETRY_EPSILON
    );
  }
  if (a.shape === 'circle' || b.shape === 'circle') {
    const circle = a.shape === 'circle' ? a : b;
    const rectangle = a.shape === 'circle' ? b : a;
    const [xAxis, yAxis] = axes(rectangle.angle);
    const offset = {
      x: circle.center!.x - rectangle.center!.x,
      y: circle.center!.y - rectangle.center!.y,
    };
    const x = dot(offset, xAxis),
      y = dot(offset, yAxis);
    const closestX = Math.max(
      -rectangle.width / 2,
      Math.min(rectangle.width / 2, x),
    );
    const closestY = Math.max(
      -rectangle.height / 2,
      Math.min(rectangle.height / 2, y),
    );
    return (
      Math.hypot(x - closestX, y - closestY) <
      circle.width / 2 - GEOMETRY_EPSILON
    );
  }
  const aAxes = axes(a.angle),
    bAxes = axes(b.angle);
  for (const axis of [...aAxes, ...bAxes]) {
    const radiusA =
      (Math.abs(dot(axis, aAxes[0])) * a.width) / 2 +
      (Math.abs(dot(axis, aAxes[1])) * a.height) / 2;
    const radiusB =
      (Math.abs(dot(axis, bAxes[0])) * b.width) / 2 +
      (Math.abs(dot(axis, bAxes[1])) * b.height) / 2;
    if (radiusA + radiusB - Math.abs(dot(delta, axis)) <= GEOMETRY_EPSILON)
      return false;
  }
  return true;
}

export function layoutFootprints(
  layout: RoomLayout,
): { id: string; footprint: Footprint }[] {
  return [
    ...Object.entries(layout.spots).map(([id, spot]) => ({
      id,
      footprint: spotFootprint(spot, layout.spotSize),
    })),
    ...layout.obstructions.map((footprint) => ({
      id: footprint.id,
      footprint,
    })),
  ];
}

/** Sweep-and-prune AABB broad phase, followed by shape-aware collision testing. */
export function layoutCollisions(layout: RoomLayout): Collision[] {
  const objects = layoutFootprints(layout)
    .map((object) => ({ ...object, bounds: footprintBounds(object.footprint) }))
    .filter(
      (object): object is typeof object & { bounds: Bounds } =>
        object.bounds !== null,
    )
    .sort((a, b) => a.bounds.x - b.bounds.x || a.id.localeCompare(b.id));
  const allowances = new Map(
    layout.overlapAllowances.map((item) => [JSON.stringify(item.ids), item]),
  );
  const active: typeof objects = [],
    collisions: Collision[] = [];
  for (const current of objects) {
    for (let i = active.length - 1; i >= 0; i--) {
      if (
        active[i].bounds.x + active[i].bounds.width - current.bounds.x <=
        GEOMETRY_EPSILON
      )
        active.splice(i, 1);
    }
    for (const other of active) {
      if (!footprintsOverlap(current.footprint, other.footprint)) continue;
      const pair = [current, other].sort((a, b) => a.id.localeCompare(b.id));
      const ids: [string, string] = [pair[0].id, pair[1].id];
      const allowance = allowances.get(JSON.stringify(ids));
      const grandfathered =
        !!allowance &&
        pair.every(
          (object, index) =>
            footprintSignature(object.footprint) ===
            allowance.footprints[index],
        );
      collisions.push({ ids, grandfathered });
    }
    active.push(current);
  }
  return collisions.sort((a, b) =>
    JSON.stringify(a.ids).localeCompare(JSON.stringify(b.ids)),
  );
}

export function layoutBounds(
  layout: Pick<RoomLayout, 'spots' | 'spotSize' | 'obstructions'>,
  reference?: Bounds,
): Bounds {
  const extents = layoutFootprints({
    ...layout,
    version: 1,
    bounds: reference ?? { x: 0, y: 0, width: 660, height: 492 },
    overlapAllowances: [],
  })
    .map(({ footprint }) => footprintBounds(footprint))
    .filter((b): b is Bounds => b !== null);
  if (reference) extents.push(reference);
  if (!extents.length) return { x: 0, y: 0, width: 660, height: 492 };
  const x = Math.min(...extents.map((b) => b.x)),
    y = Math.min(...extents.map((b) => b.y));
  const right = Math.max(...extents.map((b) => b.x + b.width)),
    bottom = Math.max(...extents.map((b) => b.y + b.height));
  return {
    x: x - PADDING,
    y: y - PADDING,
    width: right - x + PADDING * 2,
    height: bottom - y + PADDING * 2,
  };
}

/** Read-only, lossless projection: never invent a position for partially placed legacy spots. */
export function importLegacyLayout(
  spots: LegacySpot[],
  gridWidth: number | null,
  gridHeight: number | null,
): RoomLayout {
  const layout: RoomLayout = {
    version: 1,
    spotSize: null,
    spots: {},
    obstructions: [],
    bounds: { x: 0, y: 0, width: 660, height: 492 },
    overlapAllowances: [],
  };
  for (const spot of spots) {
    const placed =
      typeof spot.x === 'number' &&
      Number.isFinite(spot.x) &&
      typeof spot.y === 'number' &&
      Number.isFinite(spot.y);
    layout.spots[spot.id] = newSpotGeometry(
      'rectangle',
      placed
        ? {
            x: Math.round(spot.x!) * 110 + 52,
            y: Math.round(spot.y!) * 82 + 38,
          }
        : null,
    );
  }
  const validGrid =
    typeof gridWidth === 'number' &&
    Number.isFinite(gridWidth) &&
    gridWidth >= 1 &&
    typeof gridHeight === 'number' &&
    Number.isFinite(gridHeight) &&
    gridHeight >= 1;
  if (
    validGrid ||
    Object.values(layout.spots).some((spot) => spot.center !== null)
  ) {
    layout.bounds = layoutBounds(
      layout,
      validGrid
        ? {
            x: 0,
            y: 0,
            width: gridWidth * 110 - 6,
            height: gridHeight * 82 - 6,
          }
        : { x: 0, y: 0, width: 660, height: 492 },
    );
  }
  layout.overlapAllowances = layoutCollisions(layout).map(({ ids }) => ({
    ids,
    footprints: ids.map((id) =>
      footprintSignature(spotFootprint(layout.spots[id], null)),
    ) as [string, string],
  }));
  return layout;
}

export function retainOverlapAllowances(
  layout: RoomLayout,
  published: RoomLayout,
): OverlapAllowance[] {
  const footprints = new Map(
    layoutFootprints(layout).map(({ id, footprint }) => [
      id,
      footprintSignature(footprint),
    ]),
  );
  return published.overlapAllowances.filter((allowance) =>
    allowance.ids.every(
      (id, i) => footprints.get(id) === allowance.footprints[i],
    ),
  );
}

export function validateFootprint(footprint: Footprint): string[] {
  const errors: string[] = [];
  if (!['square', 'rectangle', 'circle'].includes(footprint.shape))
    errors.push('shape');
  if (!Number.isFinite(footprint.angle)) errors.push('angle');
  if (
    footprint.center &&
    (!Number.isFinite(footprint.center.x) ||
      !Number.isFinite(footprint.center.y))
  )
    errors.push('center');
  if (!Number.isFinite(footprint.width) || footprint.width <= 0)
    errors.push('width');
  if (!Number.isFinite(footprint.height) || footprint.height <= 0)
    errors.push('height');
  if (
    footprint.shape !== 'rectangle' &&
    Math.abs(footprint.width - footprint.height) > GEOMETRY_EPSILON
  )
    errors.push('equalSides');
  return errors;
}
